import { useEffect, useState } from 'react';
import { isNativePlatform } from '../utils/platform';

// Vertical pixels reserved below a focused input for a potential dropdown
// menu (e.g. react-select, autocomplete suggestions). If the focused input
// plus this room extends past the visible viewport once the keyboard is up,
// we scroll the input to the top so the menu has room to render.
const DROPDOWN_CLEARANCE_PX = 240;

const keyboardSubscribers = new Set<(visible: boolean) => void>();

function notifySubscribers(visible: boolean) {
  keyboardSubscribers.forEach((fn) => fn(visible));
}

export function useKeyboardVisible(): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isNativePlatform()) return;
    keyboardSubscribers.add(setVisible);
    return () => {
      keyboardSubscribers.delete(setVisible);
    };
  }, []);
  return visible;
}

function isEditable(el: Element | null): el is HTMLElement {
  if (!el || el === document.body) return false;
  return (
    (el as HTMLElement).matches(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
    ) || el.getAttribute('role') === 'combobox'
  );
}

function scrollIfObscured(el: HTMLElement): void {
  const rect = el.getBoundingClientRect();
  if (rect.bottom + DROPDOWN_CLEARANCE_PX > window.innerHeight) {
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
}

export async function configureKeyboard(): Promise<void> {
  if (!isNativePlatform()) return;

  const { Keyboard } = await import('@capacitor/keyboard');

  // Track whether the keyboard is currently visible. `keyboardDidShow` fires
  // once per open. Users can tab/next between fields without the keyboard
  // ever hiding, so a plain keyboardDidShow listener would only help the
  // first input. A document-level focusin listener gated on this flag
  // covers every subsequent field change while the keyboard is up.
  let keyboardVisible = false;

  Keyboard.addListener('keyboardDidShow', () => {
    keyboardVisible = true;
    notifySubscribers(true);
    // Defer one frame so the native webview resize has fully committed
    // and getBoundingClientRect sees the post-resize layout.
    requestAnimationFrame(() => {
      if (isEditable(document.activeElement)) {
        scrollIfObscured(document.activeElement as HTMLElement);
      }
    });
  });

  Keyboard.addListener('keyboardDidHide', () => {
    keyboardVisible = false;
    notifySubscribers(false);
  });

  document.addEventListener(
    'focusin',
    (event) => {
      if (!keyboardVisible) return;
      const target = event.target as Element | null;
      if (!isEditable(target)) return;
      // Same rAF defer as keyboardDidShow — wait for any browser-native
      // focus-scroll behavior to settle before we measure.
      requestAnimationFrame(() => scrollIfObscured(target as HTMLElement));
    },
    { capture: true }
  );
}
